import React, { useContext, useMemo, useState } from "react";
import {
  AccordionContext,
  AccordionContextResponse,
  AccordionType,
} from "./accordion-helper";
import classNames from "classnames";
import Icon from "../Icon";

interface AccordionProps {
  /**
   * Single or multiple items can be opened at the same time.
   */
  type?: AccordionType;
  /**
   * Extend classname
   */
  extendClasses?: string;
  /**
   * Default values
   */
  defaultValues?: string[];
  /**
   * Content
   */
  children?: React.ReactNode;
}

const Root: React.FC<AccordionProps> = ({
  type = "single",
  extendClasses,
  defaultValues,
  children,
}) => {
  const [values, setValues] = useState<string[]>(defaultValues || []);
  const context: AccordionContextResponse = useMemo(
    () => ({
      values,
      handleToggle: (value) =>
        setValues((previous) => {
          const isClosed = previous.includes(value);
          return type === "single"
            ? !isClosed
              ? [value]
              : []
            : isClosed
              ? previous.filter((item) => item !== value)
              : [...previous, value];
        }),
    }),
    [type, values]
  );
  return (
    <AccordionContext.Provider value={context}>
      <div
        className={classNames({
          tfcAccordion: true,
          [extendClasses as string]: !!extendClasses,
        })}
      >
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

interface AccordionItemProps {
  /**
   * Extend classname
   */
  extendClasses?: string;
  /**
   * Content
   */
  children?: React.ReactNode;
}

const Item: React.FC<AccordionItemProps> = ({ extendClasses, children }) => {
  return (
    <div
      className={classNames({
        ["tfcAccordion-item"]: true,
        [extendClasses as string]: !!extendClasses,
      })}
    >
      {children}
    </div>
  );
};

interface AccordionHeaderProps {
  /**
   * Value
   */
  value: string;
  /**
   * Extend classname
   */
  extendClasses?: string;
  /**
   * Arrow icon
   */
  arrowIcon?: React.ReactNode;
  /**
   * Content
   */
  children?: React.ReactNode;
}

const Header: React.FC<AccordionHeaderProps> = ({
  value,
  extendClasses,
  arrowIcon = <Icon icon="expandDown" size={24} color="#f05b28" />,
  children,
}) => {
  const { values, handleToggle } = useContext(AccordionContext);

  return (
    <button
      id={value}
      className={classNames({
        ["tfcAccordion-header"]: true,
        ["tfcAccordion-header-active"]: values.includes(value),
        [extendClasses as string]: !!extendClasses,
      })}
      onClick={() => handleToggle && handleToggle(value)}
    >
      {children}
      <div className="tfcAccordion-header-arrow">{arrowIcon}</div>
    </button>
  );
};

interface AccordionContentProps {
  /**
   * Extend classname
   */
  extendClasses?: string;
  /**
   * Content
   */
  children?: React.ReactNode;
}

const Content: React.FC<AccordionContentProps> = ({
  extendClasses,
  children,
}) => (
  <div
    className={classNames({
      ["tfcAccordion-content"]: true,
      [extendClasses as string]: !!extendClasses,
    })}
  >
    {children}
  </div>
);

export const Accordion = Object.assign(Root, { Item, Header, Content });
